import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

export type DevelopmentFeatureFlagDefinitionSchema =
  BaseFeatureFlagDefinition & {
    section?: Section;
    owner: string;
  };

// Feature flags moved from customer feature flags to development
export const DevelopmentFeatureFlagDataZod = zod.object({
  waypoints: zod.boolean().default(false),
  enableScrewdriverPickup: zod.boolean().default(false),
  enableLearnedModels: zod.boolean().default(false),
  captureStep: zod.boolean().default(false),
  miniArm: zod.boolean().default(false),
  newCNCIntegration: zod.boolean().default(false),
  ewellixLiftTLT: zod.boolean().default(false),
  weldMachineIntegration: zod.boolean().default(false),
  rotaryWeldStep: zod.boolean().default(true),
  enableCppGrippers: zod.boolean().default(true),
  localAccuracyCalibration: zod.boolean().default(false),
  customDevices: zod.boolean().default(false),
  customBase: zod.boolean().default(false),
  allowCustomBaseMatchJointAngles: zod.boolean().default(false),
  backups: zod.boolean().default(false),
  systemMonitorShutdown: zod.boolean().default(false),
  tooltipAcceleration: zod.boolean().default(false),
  enableWeldTooltipAccelerationEditing: zod.boolean().default(false),
  setActiveTooltip: zod.boolean().default(true),
  arcStepInMove: zod.boolean().default(true),
  disableArcStepInWeld: zod.boolean().default(false),
  forceTorqueSensor: zod.boolean().default(true),
  tactileSensor: zod.boolean().default(false),
  robotiqFt300: zod.boolean().default(false),
  antigravityToggle: zod.boolean().default(false),
  antigravityStep: zod.boolean().default(false),
  ros2Bridge: zod.boolean().default(false),
  useControlSourceManager: zod.boolean().default(true),
  disableElastokinIK: zod.boolean().default(false),
  onlinePlannerJoggingCollisionAvoidance: zod.boolean().default(true),
  useExpressionsForPayload: zod.boolean().default(false),
  allowSubstepsMoveStepV2: zod.boolean().default(false),
  disableWeldWeaving: zod.boolean().default(false),
  extraWeldModes: zod.boolean().default(true),
  millerWeldIntegrationV2: zod.boolean().default(true),
  motionPlanResampling: zod.boolean().default(false),
  treatEncoderIssuesAsWarnings: zod.boolean().default(false),
  treatPRCSyncAndInterpolatorErrorsAsWarnings: zod.boolean().default(true),
  treatShakeDetectedAsWarning: zod.boolean().default(false),
  treatWindupLUTNotCalculatedAsWarning: zod.boolean().default(false),
  motionPlanCompression: zod.boolean().default(false),
  waypointTimingScaling: zod.boolean().default(true),
  torqueAwareMotions: zod.boolean().default(false),
  usePayloadInertiaInDynamics: zod.boolean().default(true),
  useOptimizerForJogging: zod.boolean().default(false),
  enableOptimizerLinearPlanner: zod.boolean().default(true),
  stopEarlyIfForce: zod.boolean().default(true),
  disableDynamicHardwareConfig: zod.boolean().default(false),
  teleopSendHeadPose: zod.boolean().default(false),
  waypointOrientationGate: zod.boolean().default(false),
  waypointTrackerPlanTimeRefactor: zod.boolean().default(false),
  executeServerWaypointFiring: zod.boolean().default(false),
  unleashThor: zod.boolean().default(false),
  mountingBracket: zod.boolean().default(false),
  localInference: zod.boolean().default(false),
  showDummyInferenceModel: zod.boolean().default(false),
  batchJogDirectionsValidation: zod.boolean().default(true),
  externalControlStep: zod.boolean().default(false),
  miraiRunSkill: zod.boolean().default(false),
  secondaryBotWebsocketState: zod.boolean().default(false),
  appRedesign: zod.boolean().default(true),
  redesignUiStubs: zod.boolean().default(false),
  redesignDarkModeSelection: zod.boolean().default(false),
  routineUndoRedo: zod.boolean().default(true),
  routineAgentChat: zod.boolean().default(false),
  routineAgentTaskTiming: zod.boolean().default(false),
  lpfPlanner: zod.boolean().default(false),
  externalControlBridge: zod.boolean().default(false),
  persistentVariables: zod.boolean().default(true),
  disableRos2BagRecorder: zod.boolean().default(false),
  mujocoSimulator: zod.boolean().default(false),
  aiPositioningSkill: zod.boolean().default(false),
  useTrajectoryController: zod.boolean().default(true),
  externalControlRecords: zod.boolean().default(true),
  onlinePlannerJogging: zod.boolean().default(false),
  onlinePlannerStreaming: zod.boolean().default(false),
  onlinePlannerStreamLogging: zod.boolean().default(false),
  onlinePlannerCommandLogging: zod.boolean().default(false),
  onlinePlannerThor: zod.boolean().default(false),
  onlinePlannerSpark: zod.boolean().default(false),
  useVirtualArm: zod.boolean().default(false),
  useDeterministicPlanner: zod.boolean().default(true),
  legacyMotionPlanCacheTolerance: zod.boolean().default(false),
  constantSpeed: zod.boolean().default(true),
  suppressSteps: zod.boolean().default(true),
  detection3dLocate: zod.boolean().default(false),
  dinoFastClassify: zod.boolean().default(false),
  stereoDepth: zod.boolean().default(false),
  subroutines: zod.boolean().default(true),
  samplePositioningApproachStep: zod.boolean().default(false),
  insta360ImportEnabled: zod.boolean().default(false),
  collectionLabels: zod.boolean().default(false),
  matchRelativeToCurrentFrames: zod.boolean().default(true),
  routineSchemaImportExport: zod.boolean().default(false),
  useWindupTorqueV2: zod.boolean().default(true),
  useAutoTunedAntigravity: zod.boolean().default(false),
  useDynamicCollisionDetector: zod.boolean().default(false),
  useWindupTorqueForCollision: zod.boolean().default(false),
  botReservations: zod.boolean().default(false),
  armMetricsTracking: zod.boolean().default(false),
  cbMigration: zod.boolean().default(true),
});

export type DevelopmentFeatureFlagData = zod.infer<
  typeof DevelopmentFeatureFlagDataZod
>;
export type DevelopmentFeatureFlagKey = keyof DevelopmentFeatureFlagData;

export const developmentFeatureFlagDefinitions: Record<
  DevelopmentFeatureFlagKey,
  DevelopmentFeatureFlagDefinitionSchema
> = {
  disableElastokinIK: {
    title: 'Disable Elastokin IK Solver',
    dateAdded: '2026-05-21',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Fall back to the legacy ROS2 IK solver. Elastokin IK is on by default for every bot model, so this flag only needs enabling to turn it off.',
    owner: 'reyn',
  },
  onlinePlannerJoggingCollisionAvoidance: {
    // Restart-scoped: ros2-control-bot resolves the arm's collision geometry
    // from this flag at launch (collision_geometry.py), so the barrier and the
    // model MoveIt plans against only agree again once it restarts.
    requiresRestart: true,
    title: 'Online Planner Jogging: Collision Avoidance',
    dateAdded: '2026-10-02',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Collision avoidance for online planner jogging: SpeedJ and SpeedL jogs slow and stop before the arm collides with itself or its pedestal, and jog directions that would collide grey out. Also decides the collision geometry MoveIt plans against — capsules while this is on, the padded meshes while it is off — which ros2-control-bot reads at launch, so a restart is required. Only takes effect while jogs route through the trajectory controller: Online Planning (useTrajectoryController) and Online Planning for jogging (onlinePlannerJogging) on, plus onlinePlannerThor or onlinePlannerSpark on those models. Otherwise jogs take the legacy path and this flag does nothing.',
    owner: 'lovro',
  },
  waypoints: {
    requiresRestart: false,
    title: 'Old Waypoints',
    dateAdded: '2023-08-24',
    section: 'movement',
    allowSingleUser: true,
    owner: 'henry',
  },
  matchRelativeToCurrentFrames: {
    title: 'Match read/write frames for relative-to-current moves',
    dateAdded: '2026-06-04',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Relative-to-current moves read the current pose and command the target in the same TCP frame, preventing flange↔TCP frame-mismatch nosedives.',
    owner: 'alex',
  },
  routineSchemaImportExport: {
    requiresRestart: false,
    title: 'Routine Schema Import/Export',
    dateAdded: '2026-07-02',
    section: 'other',
    allowSingleUser: false,
    description:
      'Show Download/Upload buttons in the routine Edit Schema modal for saving the schema as a .json file and loading a schema from a .json/.txt file.',
    owner: 'alex',
  },
  arcStepInMove: {
    title: 'Arc Step in Move',
    dateAdded: '2026-05-22',
    description: 'Enable the Arc Step inside Move Arm steps (still under test)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  disableArcStepInWeld: {
    title: 'Disable Arc Step in Weld',
    dateAdded: '2026-05-22',
    description: 'Disable the Arc Step inside Weld steps (defaults to enabled)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  batchJogDirectionsValidation: {
    requiresRestart: false,
    title: 'Batch jog direction validation',
    dateAdded: '2026-01-26',
    description: 'Use ROS batch service to validate jog buttons',
    section: 'movement',
    allowSingleUser: true,
    owner: 'reyn',
  },
  enableScrewdriverPickup: {
    requiresRestart: false,
    title: 'OnRobot Screwdriver pickup',
    dateAdded: '2022-06-21',
    section: 'equipment',
    allowSingleUser: false,
    owner: 'paige',
  },
  enableLearnedModels: {
    title: 'Enable Learned Models',
    dateAdded: '2026-06-09',
    description: 'Enable all AI learned-model features',
    section: 'AI',
    owner: 'kavan',
  },
  captureStep: {
    title: 'Capture Step',
    dateAdded: '2025-10-24',
    description: 'Enable the Capture Step',
    section: 'vision',
    allowSingleUser: true,
    owner: 'raphael',
  },
  miniArm: {
    title: 'Mini Arm',
    dateAdded: '2023-11-20',
    description: 'Add mini-arm control',
    owner: 'connor',
  },
  newCNCIntegration: {
    title: 'New CNC integrations in equipment manager',
    dateAdded: '2024-02-05',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  ewellixLiftTLT: {
    requiresRestart: false,
    title: 'Ewellix Lift TLT',
    dateAdded: '2024-03-22',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  weldMachineIntegration: {
    requiresRestart: false,
    title: 'Weld machine integration',
    dateAdded: '2024-05-10',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'henry',
  },
  rotaryWeldStep: {
    requiresRestart: false,
    title: 'Rotary weld step',
    dateAdded: '2026-09-09',
    section: 'equipment',
    allowSingleUser: true,
    description:
      'Offer the Rotary Weld step, which welds a round part while a weld positioner rotates it under a fixed torch. Requires a configured and enabled Weld positioner under Equipment.',
    owner: 'ryanwalek',
  },
  localAccuracyCalibration: {
    title: 'Local Accuracy Calibration',
    dateAdded: '2024-09-19',
    section: 'environmentAndSpace',
    allowSingleUser: false,
    description:
      'Allows the user to calibrate the accuracy of the arm when moving to positions.',
    owner: 'shreyas',
  },
  customDevices: {
    title: 'Custom Devices',
    dateAdded: '2024-09-24',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom devices',
    owner: 'christine',
  },
  customBase: {
    title: 'Custom Base',
    dateAdded: '2025-05-04',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom robot base/mounting configuration',
    owner: 'jacob',
  },
  allowCustomBaseMatchJointAngles: {
    title: 'Allow Custom Base Match Joint Angles',
    dateAdded: '2025-09-29',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Allow to match joint angles when a custom base is enabled',
    owner: 'jacob',
  },
  backups: {
    requiresRestart: false,
    title: 'Backups',
    dateAdded: '2025-01-03',
    section: 'infra',
    allowSingleUser: false,
    description: 'Show backups option in robot menu',
    owner: 'shreyas',
  },
  systemMonitorShutdown: {
    title: 'System Monitor Shutdown',
    dateAdded: '2025-01-29',
    section: 'safety',
    allowSingleUser: false,
    description: 'Shut down control box when system reaches critical levels',
    owner: 'shreyas',
  },
  tooltipAcceleration: {
    requiresRestart: false,
    title: 'Tooltip Acceleration',
    dateAdded: '2025-03-12',
    section: 'movement',
    description: 'Enable tooltip acceleration controls for safety settings',
    owner: 'henry',
  },
  enableWeldTooltipAccelerationEditing: {
    requiresRestart: false,
    title: 'Weld Tooltip Acceleration Editing',
    dateAdded: '2026-03-24',
    section: 'movement',
    description:
      'Allow editing tooltip acceleration in weld steps. When off, acceleration is fixed at 50 mm/s².',
    owner: 'henry',
  },
  setActiveTooltip: {
    title: 'Set Active Tooltip',
    dateAdded: '2026-08-10',
    section: 'movement',
    allowSingleUser: false,
    description:
      'User-set Active Tooltip: the jog selector, routine steps, and the public API set a durable active tooltip that motion steps never change.',
    owner: 'rox',
  },
  antigravityToggle: {
    requiresRestart: false,
    title: 'Antigravity Toggle',
    dateAdded: '2025-03-31',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable non-wrist antigravity toggle',
    owner: 'paige',
  },
  antigravityStep: {
    title: 'Antigravity Step',
    dateAdded: '2026-01-15',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable Antigravity Step',
    owner: 'jacob',
  },
  forceTorqueSensor: {
    title: 'Force/Torque Sensors',
    dateAdded: '2025-03-25',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable force/torque sensor integrations (OnRobot Hex-E, and future force/torque sensors)',
    owner: 'jacob',
  },
  tactileSensor: {
    title: 'Tactile Sensor',
    dateAdded: '2026-08-20',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable Robotiq TSF-85 tactile sensor fingertips',
    owner: 'jacob',
  },
  robotiqFt300: {
    requiresRestart: false,
    title: 'Robotiq FT-300',
    dateAdded: '2026-09-16',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Offer the Robotiq FT-300 force/torque sensor as equipment. Held separate from forceTorqueSensor so the HEX-E can ship on by default while the FT-300 stays opt-in.',
    owner: 'jacob',
  },
  ros2Bridge: {
    title: 'ROS2 Bridge',
    dateAdded: '2025-05-09',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable `ros2-bridge-bot` to bridge ROS2 topics to the local network',
    owner: 'shreyas',
  },
  useControlSourceManager: {
    title: 'Control Source Manager',
    dateAdded: '2025-07-14',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the control source manager to manage what pose source can control the arm. ' +
      'Exposed to customers, inverted, as the "Keep using the deprecated ROS2 API" switch in Settings → External control → Developer API. ' +
      'That switch writes only this flag. The customer copy never names the manager. ' +
      'On by default, so that switch rests off and a robot is on the External Control streaming API unless someone opts back in.',
    owner: 'connor',
  },
  enableCppGrippers: {
    title: 'C++ Gripper Control',
    dateAdded: '2026-01-27',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable low-latency C++ gripper control in ACB instead of TypeScript drivers (on by default)',
    owner: 'aneek',
  },
  useExpressionsForPayload: {
    requiresRestart: false,
    title: 'Use Expressions for Payload',
    dateAdded: '2025-09-02',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable use of expressions for payload',
    owner: 'reyn',
  },
  allowSubstepsMoveStepV2: {
    title: 'Allow certain new substeps in move step v2',
    dateAdded: '2025-09-04',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable allow set io and set variable move step v2',
    owner: 'reyn',
  },
  disableWeldWeaving: {
    requiresRestart: false,
    title: 'Disable Weld Weaving',
    dateAdded: '2026-05-21',
    section: 'movement',
    allowSingleUser: true,
    description:
      'Disable the weaving option in the weld step wizard (defaults to enabled on Core, disabled on Thor)',
    owner: 'santiago',
  },
  extraWeldModes: {
    requiresRestart: false,
    title: 'Extra Weld Modes',
    dateAdded: '2026-07-31',
    section: 'equipment',
    allowSingleUser: true,
    description:
      'Offer the HD MIG and RMD processes in the Weld step wizard (on by default). A step already saved with one of them keeps offering it regardless of this flag.',
    owner: 'ryanwalek',
  },
  millerWeldIntegrationV2: {
    requiresRestart: false,
    title: 'Miller Weld Integration V2',
    dateAdded: '2026-08-03',
    section: 'equipment',
    allowSingleUser: true,
    description:
      'Second-generation Miller weld machine integration (on by default). Implements preflow/postflow settings amongst other visual changes.',
    owner: 'ryanwalek',
  },
  motionPlanResampling: {
    title: 'Motion Plan Resampling',
    dateAdded: '2025-09-05',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable resampling of weaving motion plans',
    owner: 'santiago',
  },
  treatEncoderIssuesAsWarnings: {
    title: 'Treat Encoder Issues as Warnings',
    dateAdded: '2025-09-09',
    section: 'safety',
    allowSingleUser: false,
    description: 'Treat encoder issues as warnings instead of errors',
    owner: 'paige',
  },
  treatPRCSyncAndInterpolatorErrorsAsWarnings: {
    title: 'Treat PRC Sync and Interpolator Error as Warnings',
    dateAdded: '2026-01-15',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat PRC sync and interpolator error as warnings instead of errors',
    owner: 'paige / ryan',
  },
  treatShakeDetectedAsWarning: {
    title: 'Treat Shake Detected as Warning',
    dateAdded: '2026-02-18',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat ShakeDetected ResultType as a warning instead of an error',
    owner: 'ryan',
  },
  treatWindupLUTNotCalculatedAsWarning: {
    title: 'Treat Windup LUT Not Calculated as Warning',
    dateAdded: '2026-04-14',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat missing windup calibration (WindupLUTNotCalculated) as a warning instead of a violation',
    owner: 'aneek',
  },
  motionPlanCompression: {
    title: 'Motion Plan Compression',
    dateAdded: '2025-09-10',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable compression for motion plan cache',
    owner: 'freddy',
  },
  waypointTimingScaling: {
    title: 'Different speed per waypoints',
    dateAdded: '2025-12-23',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable per-waypoint timing scaling control in Move Arm',
    owner: 'lovro',
  },
  torqueAwareMotions: {
    title: 'Torque-aware motions',
    dateAdded: '2025-09-15',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable motion planning using torque limits',
    owner: 'jacob',
  },
  usePayloadInertiaInDynamics: {
    requiresRestart: false,
    title: 'Payload inertia in dynamics',
    dateAdded: '2026-06-08',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Model payload as end-effector segment inertia so KDL inverse-dynamics accounts for both gravity (m·g) and inertial (m·r²·α) contributions on every joint, instead of only applying a static m·g wrench at the EE. Affects expectedTorque used by windup-cal training and collision detection.',
    owner: 'brian',
  },
  useOptimizerForJogging: {
    title: 'Use Optimizer for Jogging',
    dateAdded: '2026-03-12',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the path-preserving optimizer instead of TOTG for jogging time parametrization. Preserves exact waypoint positions without corner-rounding or resampling.',
    owner: 'lovro',
  },
  enableOptimizerLinearPlanner: {
    title: 'Optimizer Linear Planner',
    dateAdded: '2026-02-10',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Include OptimizerLinearPlanner in the multi-planner for TYPE_LINEAR connections',
    owner: 'reyn',
  },
  stopEarlyIfForce: {
    title: 'Stop Early If Force',
    dateAdded: '2025-10-08',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable stop early if force for waypoints',
    owner: 'rox',
  },
  disableDynamicHardwareConfig: {
    title: 'Disable Dynamic Hardware Config',
    dateAdded: '2026-04-10',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Disable loading dynamic hardware config from firmware (defaults to enabled)',
    owner: 'aneek',
  },
  teleopSendHeadPose: {
    title: 'Teleop Head Pose UDP',
    dateAdded: '2025-10-13',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable POC head tracking via udp',
    owner: 'cnr',
  },
  waypointOrientationGate: {
    title: 'Waypoint Orientation Gate',
    dateAdded: '2025-11-18',
    description:
      'Require orientation within thresholds (in addition to distance) to mark waypoint reached',
    section: 'movement',
    allowSingleUser: false,
    owner: 'derekhe',
  },
  waypointTrackerPlanTimeRefactor: {
    title: 'Waypoint Tracker Plan Time Refactor',
    dateAdded: '2026-08-04',
    description:
      'Fire waypoint-reached events from the ROS2 execute server instead of pose-based inference',
    section: 'movement',
    allowSingleUser: false,
    owner: 'reyn',
  },
  executeServerWaypointFiring: {
    title: 'Execute Server Waypoint Firing',
    dateAdded: '2026-08-04',
    description:
      'Fire waypoint-reached events from the ROS2 execute server instead of pose-based inference',
    section: 'movement',
    allowSingleUser: false,
    owner: 'reyn',
  },
  unleashThor: {
    title: 'Unleash Thor',
    dateAdded: '2026-06-04',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Enables the Faster safety preset and unlocks the full speed/acceleration range for Thor arms.',
    owner: 'aneek',
  },
  mountingBracket: {
    requiresRestart: false,
    title: 'Mounting Bracket',
    dateAdded: '2025-12-22',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable mounting bracket end effector equipment for mounting adapters',
    owner: 'jacob',
  },
  localInference: {
    title: 'Local Inference',
    dateAdded: '2026-06-11',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable running AI inference locally (on GPU when present, otherwise CPU) without an external GPU Task Runner',
    owner: 'derek',
  },
  showDummyInferenceModel: {
    requiresRestart: false,
    title: 'Show dummy inference model',
    dateAdded: '2026-03-10',
    section: 'AI',
    allowSingleUser: true,
    description:
      'Show a dummy circle model option in the model selector for camera-free sim testing',
    owner: 'kavan',
  },
  externalControlStep: {
    title: 'External Control Step',
    dateAdded: '2026-02-10',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable external control step',
    owner: 'jacob',
  },
  miraiRunSkill: {
    title: 'MIRAI Run Skill',
    dateAdded: '2026-02-27',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable MIRAI Controller equipment and MIRAI Run Skill step',
    owner: 'jacob',
  },
  secondaryBotWebsocketState: {
    title: 'Secondary Bot WebSocket State',
    dateAdded: '2026-02-12',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable subscribing to secondary bot RoutineRunnerState via WebSocket in StandardBotsRO1 integration',
    owner: 'michael',
  },
  appRedesign: {
    requiresRestart: false,
    title: 'App Redesign',
    dateAdded: '2026-02-26',
    section: 'newUI',
    allowSingleUser: false,
    description: 'Enable the redesigned app UI',
    owner: 'christine',
  },
  redesignUiStubs: {
    requiresRestart: false,
    title: '[Eng only] Post-Automate Redesign UI Stubs',
    dateAdded: '2026-06-09',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Show not-yet-functional redesign UI (Simulate/Step/Skip-step/Anti-Gravity buttons, Safety/Network/Help settings tabs, and placeholder joint/motion metrics). Requires `appRedesign`. When off, these stubs are hidden.',
    owner: 'christine',
  },
  redesignDarkModeSelection: {
    requiresRestart: false,
    title: 'Redesign Dark Mode Selection',
    dateAdded: '2026-08-26',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Show the Light/Dark appearance selector in the redesigned General settings. Requires `appRedesign` to also be on. When off, the selector is hidden and the appearance preference is left as-is.',
    owner: 'christine',
  },
  routineUndoRedo: {
    requiresRestart: false,
    title: 'Routine Editor Undo/Redo',
    dateAdded: '2026-08-17',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Record routine edits and show Undo/Redo in the routine menu. When off, edits are written exactly as before but nothing is recorded and the menu items are hidden.',
    owner: 'christine',
  },
  routineAgentChat: {
    requiresRestart: false,
    title: 'Routine Agent Chat',
    dateAdded: '2026-05-28',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Enable the in-app Routine Agent chat panel. Requires `appRedesign` to also be on — the chat panel only mounts inside the redesign layout.',
    owner: 'santiago',
  },
  routineAgentTaskTiming: {
    requiresRestart: false,
    title: 'Advanced Agent Feedback',
    dateAdded: '2026-09-16',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Let applications engineers and selected beta customers log task-time estimates in Routine Agent chat. Requires Routine Agent Chat.',
    owner: 'zaina',
  },
  lpfPlanner: {
    title: 'LPF Planner',
    dateAdded: '2026-03-04',
    section: 'movement',
    description:
      'Use low-pass filter planner instead of spline planner for streaming interfaces',
    owner: 'Alex',
  },
  persistentVariables: {
    title: 'Persistent Variables',
    dateAdded: '2026-03-11',
    section: 'environmentAndSpace',
    description:
      'Allow environment variables to retain their last runtime value across routine executions',
    owner: 'roxanne',
  },
  armMetricsTracking: {
    title: 'Enable Arm Metrics Tracking',
    dateAdded: '2026-03-13',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable robodometer reporting for metrics tracking',
    owner: 'shreyas',
  },
  disableRos2BagRecorder: {
    title: 'Disable ROS2 Bag Recorder',
    dateAdded: '2026-05-15',
    section: 'movement',
    allowSingleUser: false,
    description:
      'When enabled, forces the ROS2 bag recorder off on control boxes (where it would otherwise run by default). No effect in dev/VM environments.',
    owner: 'reyn',
  },
  mujocoSimulator: {
    title: 'MuJoCo Simulator',
    dateAdded: '2026-03-30',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable MuJoCo simulator features: virtual camera discovery, scene object API for codeblocks',
    owner: 'lee',
  },
  externalControlBridge: {
    title: 'External Control Bridge',
    dateAdded: '2026-03-09',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable UDP real-time streaming bridge in external-control-bot',
    owner: 'jacob',
  },
  constantSpeed: {
    requiresRestart: false,
    title: 'Constant Speed',
    dateAdded: '2026-04-13',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Enable constant speed mode for Move Arm steps, routing motions through the Cartesian planner',
    owner: 'santiago',
  },
  aiPositioningSkill: {
    requiresRestart: false,
    title: 'AI Positioning Skill',
    dateAdded: '2026-06-09',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Gate the ability for the skill wizard to create a positioning skill',
    owner: 'kavan',
  },
  useTrajectoryController: {
    title: 'Online Planning',
    dateAdded: '2026-04-01',
    section: 'movement',
    description:
      'Online planning master switch: enables the 1kHz trajectory controller in arm-control-bot (module, connector, prerequisite pushes). Consumers opt in per feature via Online Planning — Jogging and Online Planning — Streaming. On by default on RO1; Thor and Spark stay on the legacy path until their own Online Planning flag is on.',
    owner: 'freddy',
  },
  externalControlRecords: {
    title: 'External Control — Fault & Diagnostic Records',
    dateAdded: '2026-08-31',
    section: 'movement',
    description:
      'Publish customer-facing fault and diagnostic records to external-control clients (SDK current_fault, the ROS fault/events topics). On by default; turn off to stop botman authoring records. Deliberately independent of Online Planning (useTrajectoryController): the trajectory-controller half of the record stream needs it, but robot safety faults — E-stop, collision, out-of-joint-limits, safety watchdog — do not, and clients on the pose or jointTrajectory paths never touch the controller at all. With this on and Online Planning off, clients still get safety faults; TC diagnostics simply have no producer.',
    owner: 'freddy',
  },
  onlinePlannerJogging: {
    title: 'Online Planning — Jogging',
    dateAdded: '2026-07-29',
    section: 'movement',
    description:
      'Online planning for jogging: route SpeedJ/SpeedL jogs through the trajectory controller. Off by default, and only takes effect when Online Planning (useTrajectoryController) is on.',
    owner: 'freddy',
  },
  onlinePlannerStreaming: {
    title: 'Online Planning — Streaming',
    dateAdded: '2026-07-28',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Online planning for external-control streaming: route joint streaming through the trajectory controller (ServoJ) instead of the direct WriteJointTrajectory path, and pose streaming through ServoL instead of the direct WritePose path. Requires Online Planning (useTrajectoryController) and zmqPoseMigration. On the TC path a streamed pose commands the tool centre point.',
    owner: 'freddy',
  },
  onlinePlannerStreamLogging: {
    title: 'Online Planning — Stream Logging',
    dateAdded: '2026-08-05',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Record the ServoJ setpoints an external-control streaming session sends to the trajectory controller into the blackbox bag (sink/WriteServoJ). Diagnostic only — it adds a publish per setpoint on the thread that delivers them, so leave it off outside an investigation. Needs Online Planning — Streaming (onlinePlannerStreaming) to have a stream to record, and the ZMQ Bag Recorder (zmqBagRecorder) to record it.',
    owner: 'freddy',
  },
  onlinePlannerCommandLogging: {
    title: 'Online Planning — Command Logging',
    dateAdded: '2026-08-18',
    section: 'movement',
    allowSingleUser: false,
    description:
      "Record every command the trajectory controller produces — all 1000 per second — into the blackbox bag (trajectorycontroller/CommandedState). This is what the controller commanded, before arm-control-bot's 200 Hz hardware loop resamples it onto CAN, so it is the only way to see whether a discontinuity in /CombinedDesires is really in the commanded motion or just an artifact of that resampling. Diagnostic only: with it on, the shb_ blackbox stream measures about 1.8 MB per 30-second file (~0.2 GB/hour, LZ4-compressed on disk) — so turn it on for an investigation and back off afterwards. Takes effect on the next arm-control-bot restart. Needs Online Planning (useTrajectoryController) to have a controller running, and the ZMQ Bag Recorder (zmqBagRecorder) to record it.",
    owner: 'freddy',
  },
  onlinePlannerThor: {
    title: 'Online Planning — Thor',
    dateAdded: '2026-09-16',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Let Thor arms reach the online planner. Thor is supported — it has its own elastokin model, jog tuning and limits — but the planner has not been validated end to end on it, so it stays off until the arm is signed off. Every Online Planning flag reads as off on Thor unless this is also on, so arm-control-bot never starts the controller and nothing routes to it. Spark has its own flag; RO1 and RO2 are unaffected. Takes effect on the next arm-control-bot restart: turning it off while a controller is already running leaves that controller owning the arm while the rest of the stack has gone back to the legacy path, and nothing moves until the restart.',
    owner: 'freddy',
  },
  onlinePlannerSpark: {
    title: 'Online Planning — Spark',
    dateAdded: '2026-09-16',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Let Spark arms reach the online planner. Spark is supported — it has its own elastokin model, jog tuning and limits — but the planner has not been validated end to end on it, so it stays off until the arm is signed off. Every Online Planning flag reads as off on Spark unless this is also on, so arm-control-bot never starts the controller and nothing routes to it. Thor has its own flag; RO1 and RO2 are unaffected. Takes effect on the next arm-control-bot restart: turning it off while a controller is already running leaves that controller owning the arm while the rest of the stack has gone back to the legacy path, and nothing moves until the restart.',
    owner: 'freddy',
  },
  useVirtualArm: {
    title: 'Virtual Arm in arm-control-bot',
    dateAdded: '2026-05-07',
    section: 'movement',
    description:
      'Move the canonical source of truth for vizbot joint state into a SimulatedJointState container inside arm-control-bot. Botman pushes positions on every state change (jog, lerp tick, teleport) and subscribes to a JointState topic to keep its local cache in sync. Phase A — no TrajectoryController involvement yet.',
    owner: 'connor',
  },
  useDeterministicPlanner: {
    title: 'Maintain robot configuration',
    dateAdded: '2026-05-01',
    section: 'movement',
    allowSingleUser: true,
    description:
      'Show the "Maintain robot configuration" toggle on every waypoint regardless of motion kind (joint, line, rotation). When the toggle is on: joint motions use the deterministic Pilz PTP planner (no RRT fallback), and every motion restricts IK to the upstream waypoint\'s configuration branch (overhead position / elbow up-or-down / wrist sign) so the arm holds the same configuration end-to-end.',
    owner: 'reyn',
  },
  legacyMotionPlanCacheTolerance: {
    title: 'Legacy Motion Plan Cache Tolerance',
    dateAdded: '2026-04-06',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use tighter start joint tolerance (0.002 rad) for the motion plan cache, for old firmware with less position settling tolerance',
    owner: 'reyn-sb',
  },
  suppressSteps: {
    requiresRestart: false,
    title: 'Suppress Steps',
    dateAdded: '2026-04-20',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Allow suppressing routine steps so they remain in the tree but are skipped during execution.',
    owner: 'ryanwalek',
  },
  detection3dLocate: {
    requiresRestart: false,
    title: '3D Detection Locate',
    dateAdded: '2026-04-20',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Enable 3D detection locate method using text prompt + depth-based OBB pose estimation',
    owner: 'raphael',
  },
  dinoFastClassify: {
    requiresRestart: false,
    title: 'Match AI Classify',
    dateAdded: '2026-05-20',
    section: 'vision',
    allowSingleUser: true,
    description:
      'Enable Match AI classify method (image classification via full-image embeddings and cosine similarity)',
    owner: 'kavan',
  },
  stereoDepth: {
    title: 'Stereo Depth',
    dateAdded: '2026-04-22',
    section: 'vision',
    allowSingleUser: false,
    description: 'Enable depth processing for stereo camera pairs',
    owner: 'derek',
  },
  subroutines: {
    title: 'Subroutines',
    dateAdded: '2026-04-27',
    section: 'movement',
    description:
      'Allow calling other routines as subroutines from within a routine',
    owner: 'roxanne',
  },
  samplePositioningApproachStep: {
    title: 'Sample Positioning Approach Step',
    dateAdded: '2026-05-18',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the SamplePositioningApproach step (positioning-skill data collection)',
    owner: 'kavan',
  },
  insta360ImportEnabled: {
    title: 'Insta360 SD-card Upload Pipeline',
    dateAdded: '2026-06-02',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable echo-bot HandheldImportService — polls /etc/standardbots/skills/echo-recording/insta360-upload/ for .insv recordings, looks up per-camera calibration on feathers-bot, and routes them through UploadManager into the cloud SLAM stage.',
    owner: 'derek',
  },
  collectionLabels: {
    title: 'Collection Labels',
    dateAdded: '2026-08-07',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Collection labeling on the Create Recording step: shows the "Prompt for label" editor option, and gates the end-of-recording prompt at runtime (disabling the flag also silences routines already configured to prompt). The recorder is stopped before the prompt opens, so no stationary frames are captured while the operator answers; the chosen label travels with the follow-up save into the recording metadata and the cloud session.',
    owner: 'dkim',
  },
  useWindupTorqueV2: {
    title: 'Use Windup Torque V2 (ML)',
    dateAdded: '2026-07-14',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Master enable for the windup-torque V2 (ML estimate) pipeline. Only joints whose per-arm config sets `windupTorqueV2Capable: true` will actually consume the V2 signal; joints not opted in keep the legacy `windupTorque` regardless of this flag. Joints that opt in but whose firmware reports an UNCALIBRATED ML model fall back to the legacy `windupTorque` at runtime and emit a WARNING event so the operator can flash a model. Disable to force ALL joints back to legacy.',
    owner: 'brian',
  },
  useAutoTunedAntigravity: {
    title: 'Auto-tuned antigravity',
    dateAdded: '2026-07-14',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Replace the static per-joint antigravity disturbance deadband with one auto-tuned to the live windup-torque noise floor, and switch the in-AG baseline between a stationary capture (e_static) and a moving capture (e_moving) to absorb the windupTorqueV2 stationary-vs-moving bias step. Off by default falls back to the legacy static-threshold + single-baseline path.',
    owner: 'brian',
  },
  useDynamicCollisionDetector: {
    title: 'Dynamic collision detector',
    dateAdded: '2026-07-14',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot replaces the static per-joint collision threshold with a lagged moving-average detector: threshold(t) = min(MA(|d|, [t-lag-window, t-lag]) + headroomNm, maxThresholdNm). The MA tracks the natural pose-dependent disturbance envelope (the V2 windup model is more accurate during motion than static hold); subtracting it leaves only the spike component, so the headroom directly sets the minimum detectable collision magnitude (~20 Nm by default). Sustained collisions stay latched because the MA freezes when above threshold. Per-joint: headroomNm, maxThresholdNm; arm-wide: lagMs, windowMs. Orthogonal to the windup-vs-amperage torque-source flag.',
    owner: 'brian',
  },
  useWindupTorqueForCollision: {
    title: 'Compare collision disturbance against windup torque',
    dateAdded: '2026-07-14',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot uses windupTorque instead of amperageTorque as the measured-torque source feeding the collision-detection comparison. Only joints whose per-arm config sets `windupTorqueForCollisionCapable: true` switch; joints not opted in keep amperageTorque regardless of this flag. Switches which disturbance signal is published downstream as well so logs stay consistent. Orthogonal to the threshold-strategy flag (useDynamicCollisionDetector).',
    owner: 'brian',
  },
  botReservations: {
    title: 'Bot Reservations',
    dateAdded: '2026-08-12',
    section: 'infra',
    owner: 'niki',
    allowSingleUser: true,
    // UI-only: gates the nav-bar surface; the controlLock backend registers
    // unconditionally and is inert without data.
    requiresRestart: false,
    description:
      'Reservation information on nav-bar. Used so ATI can reserve a robot',
  },
  cbMigration: {
    title: 'CB Backup/restore',
    dateAdded: '2026-08-28',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Control-box backup and restore against the cloud checkpoint index: ' +
      'the scheduled backup captures the complete robot state ' +
      '(database, configuration, blob store, code blocks, manifest) ' +
      'instead of the database only, and the robot settings ' +
      'gain an on-demand "Back Up to Cloud" action. On by default; the ' +
      'schedule itself is the backups.cadence setting, which defaults to ' +
      'never, so a box only backs up on its own when an operator picks a ' +
      'cadence or backs up on demand.',
    owner: 'michael',
  },
};

type GroupedKeys = { section: Section; keys: DevelopmentFeatureFlagKey[] }[];

export const underDevelopmentFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(developmentFeatureFlagDefinitions)) {
    const key = keyStr as DevelopmentFeatureFlagKey;
    let { section } = developmentFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
